"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, Pipette, Search, Wrench, Droplet, Layers, Sparkles, PhoneCall, Clock, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const servicesList = [{
  id: "emergency-plumbing",
  title: "Emergency Plumbing",
  badge: "24/7 Fast Response",
  description: "Urgent burst pipes, major flooding, and critical failures handled immediately by on-call plumbers.",
  link: "/emergency-plumbing/",
  iconName: "alert"
}, {
  id: "drain-cleaning",
  title: "Drain Cleaning",
  badge: "Same-Day Service",
  description: "Comprehensive clearing of stubborn clogs, main sewer line stoppages, and backed-up fixtures.",
  link: "/drain-cleaning/",
  iconName: "sparkles"
}, {
  id: "water-heater",
  title: "Water Heater Repair",
  badge: "Gas & Electric",
  description: "Diagnostics, heating element replacement, thermostat fixes, and full tankless water heater maintenance.",
  link: "/water-heater-repair/",
  iconName: "flame"
}, {
  id: "leak-detection",
  title: "Leak Detection & Repair",
  badge: "Non-Invasive",
  description: "Pinpoint accuracy electronic slab leak detection, wall pipe inspection, and rapid leak mitigation.",
  link: "/leak-detection-repair/",
  iconName: "search"
}, {
  id: "toilet-repair",
  title: "Toilet Repair",
  badge: "Clog & Leak Fix",
  description: "Fixing running mechanisms, constant overflow, cracked bowls, and faulty wax ring seals reliably.",
  link: "/toilet-repair/",
  iconName: "wrench"
}, {
  id: "sewer-line",
  title: "Sewer Line Repair",
  badge: "Trenchless Options",
  description: "Camera video inspections, root intrusion clearing, sewer pipe relining, and trenchless replacements.",
  link: "/sewer-line-repair/",
  iconName: "layers"
}, {
  id: "pipe-repair",
  title: "Pipe Repair & Repiping",
  badge: "Copper & PEX",
  description: "Corrosion treatment, burst pipe welding, freeze damage remediation, and whole-home repiping solutions.",
  link: "/pipe-repair/",
  iconName: "pipette"
}, {
  id: "faucet-sink",
  title: "Faucet & Sink Repair",
  badge: "Kitchen & Bath",
  description: "Dripping tap fixes, vanity sink trap replacements, garbage disposal unjamming, and new fixture setups.",
  link: "/faucet-sink-repair/",
  iconName: "droplet"
}];
function getServiceIcon(iconName: string) {
  switch (iconName) {
    case "alert":
      return <AlertTriangle className="size-6 text-primary" />;
    case "sparkles":
      return <Sparkles className="size-6 text-primary" />;
    case "flame":
      return <Flame className="size-6 text-primary" />;
    case "search":
      return <Search className="size-6 text-primary" />;
    case "wrench":
      return <Wrench className="size-6 text-primary" />;
    case "layers":
      return <Layers className="size-6 text-primary" />;
    case "pipette":
      return <Pipette className="size-6 text-primary" />;
    case "droplet":
    default:
      return <Droplet className="size-6 text-primary" />;
  }
}
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="coreservicesgrid" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3.5 px-3 py-1 font-semibold text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border-border">
            <Clock className="size-3.5 mr-1 text-accent" />
            24/7 Immediate Plumbing Solutions
          </Badge>

          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Complete Emergency & Scheduled Services
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            From sudden midnight leaks and flooded basements to routine fixture installations, our licensed Jonesboro technicians arrive equipped to solve your plumbing problem on the first visit.
          </p>
        </div>

        {/* 8-Card Triage Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesList.map((service, i) => <motion.div key={service.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: i * 0.05
        }} className="h-full flex">
              <Card className="group relative flex flex-col justify-between w-full rounded-xl border border-border bg-card text-card-foreground shadow-xs hover:shadow-lg hover:border-primary/40 transition-[box-shadow,border-color] duration-300 overflow-hidden">
                <CardHeader className="pb-3 pt-6 px-6 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center justify-center size-12 rounded-lg bg-secondary text-secondary-foreground shadow-2xs group-hover:bg-primary/10 transition-colors duration-200">
                      {getServiceIcon(service.iconName)}
                    </div>
                    <Badge variant="outline" className="text-[11px] font-medium border-border/80 bg-background/80 text-muted-foreground">
                      {service.badge}
                    </Badge>
                  </div>

                  <CardTitle className="font-default text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-200 pt-1">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 py-0 flex-1">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground font-sans">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-5 pb-6 px-6 border-t border-border/60 mt-4">
                  <Link to={service.link} className="inline-flex items-center text-sm font-semibold text-primary group-hover:text-accent-foreground transition-colors group/link w-full justify-between">
                    <span>Learn details</span>
                    <span className="flex items-center justify-center size-7 rounded-md bg-secondary group-hover/link:bg-accent group-hover/link:text-accent-foreground transition-colors">
                      <ArrowRight className="size-4 transition-transform group-hover/link:translate-x-0.5" />
                    </span>
                  </Link>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Fast Action Banner */}
        <div className="mt-12 md:mt-16 rounded-2xl bg-secondary/70 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start sm:items-center gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-xs">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="font-default text-lg sm:text-xl font-bold text-foreground">
                Don't See Your Specific Plumbing Issue?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                We handle all residential and commercial plumbing systems in Jonesboro and surrounding areas.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-start sm:justify-end">
            <Button asChild variant="outline" size="default" className="border-border bg-background hover:bg-muted font-semibold w-full sm:w-auto">
              <Link to="/problems/">
                View All Problems
              </Link>
            </Button>
            <Button asChild variant="default" size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold w-full sm:w-auto">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-1.5" />
                Get Immediate Help
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}